/**
 * 权限管理页面
 * 对接后端 system_app API
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  message,
  InputNumber,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  LockOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_BASE_URL, API_ENDPOINTS } from '../../api/config';

interface Permission {
  id: number;
  name: string;
  code: string;
  type: string;
  parent_id: number;
  path?: string;
  icon?: string;
  sort: number;
  status: number;
  created_at?: string;
}

const PermissionList: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  const getToken = () => localStorage.getItem('token') || '';

  useEffect(() => {
    loadPermissions();
  }, []);

  // 加载权限列表
  const loadPermissions = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.system_app.permissions}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPermissions(data.permissions || []);
      } else {
        const error = await response.json();
        message.error(error.detail || '加载失败');
      }
    } catch (error) {
      message.error('网络错误');
    } finally {
      setLoading(false);
    }
  };

  // 新增权限
  const handleAdd = () => {
    form.resetFields();
    form.setFieldsValue({ type: 'menu', sort: 0 });
    setModalVisible(true);
  };

  // 提交新增
  const handleSubmit = async (values: any) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.system_app.permissions}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(values),
        }
      );

      if (response.ok) {
        message.success('新增成功');
        setModalVisible(false);
        loadPermissions();
      } else {
        const error = await response.json();
        message.error(error.detail || '新增失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const columns: ColumnsType<Permission> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: '权限名称',
      dataIndex: 'name',
      width: 150,
    },
    {
      title: '权限编码',
      dataIndex: 'code',
      width: 180,
    },
    {
      title: '类型',
      dataIndex: 'type',
      width: 80,
      render: (type: string) => {
        const typeMap: Record<string, { color: string; text: string }> = {
          menu: { color: 'blue', text: '菜单' },
          button: { color: 'green', text: '按钮' },
          api: { color: 'orange', text: '接口' },
        };
        const config = typeMap[type] || { color: 'default', text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '路径',
      dataIndex: 'path',
      width: 150,
      render: (path: string) => path || '-',
    },
    {
      title: '排序',
      dataIndex: 'sort',
      width: 70,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status: number) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 160,
      render: (time: string) =>
        time ? new Date(time).toLocaleString('zh-CN') : '-',
    },
  ];

  const permissionTypeOptions = [
    { label: '菜单权限', value: 'menu' },
    { label: '按钮权限', value: 'button' },
    { label: '接口权限', value: 'api' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <LockOutlined />
            <span>权限管理</span>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadPermissions}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增权限
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={permissions}
          rowKey="id"
          loading={loading}
          pagination={{
            total: permissions.length,
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* 新增权限弹窗 */}
      <Modal
        title="新增权限"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="权限名称"
            name="name"
            rules={[{ required: true, message: '请输入权限名称' }]}
          >
            <Input placeholder="如：用户管理" />
          </Form.Item>

          <Form.Item
            label="权限编码"
            name="code"
            rules={[{ required: true, message: '请输入权限编码' }]}
          >
            <Input placeholder="如：system:user:list" />
          </Form.Item>

          <Form.Item
            label="权限类型"
            name="type"
            rules={[{ required: true, message: '请选择权限类型' }]}
          >
            <Select options={permissionTypeOptions} />
          </Form.Item>

          <Form.Item label="路径" name="path">
            <Input placeholder="前端路由路径，如：/users" />
          </Form.Item>

          <Form.Item label="排序" name="sort">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit">
                确定
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PermissionList;
